import { Anchor, Badge, Button, Group, Table, Text, Tooltip } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';

import { useTemplates } from '../api/hooks';
import { DataState } from '../components/DataState';
import { PageHeader } from '../components/PageHeader';
import { CHANNEL_LABEL, fmtRelative } from '../theme/format';

const DLT_COLOR: Record<string, string> = {
  approved: 'teal', pending: 'yellow', rejected: 'red', not_required: 'gray',
};

export function TemplatesPage() {
  const { data, isLoading, error } = useTemplates();
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        title="Templates"
        description="Liquid content per channel. Rendered per recipient, wrapped in your branding, with an unsubscribe link added automatically to marketing email."
        action={
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/templates/new')}>
            New template
          </Button>
        }
      />

      <DataState
        loading={isLoading}
        error={error}
        empty={!data?.length}
        emptyTitle="No templates yet"
        emptyDescription="Email, SMS, and WhatsApp need one. Push can go out with inline content."
        action={<Button onClick={() => navigate('/templates/new')}>Write one</Button>}
      >
        <Table.ScrollContainer minWidth={760}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Channel</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Registration</Table.Th>
                <Table.Th>Updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data?.map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td>
                    <Anchor component={Link} to={`/templates/${t.id}`} fw={500} size="sm">
                      {t.name}
                    </Anchor>
                    {t.subject && <Text size="xs" c="dimmed" lineClamp={1}>{t.subject}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <Badge variant="light">{CHANNEL_LABEL[t.channel] ?? t.channel}</Badge>
                      {t.channel === 'sms' && t.sms_segments ? (
                        <Tooltip
                          label={
                            t.sms_unicode
                              ? 'Contains non-GSM characters, so each segment holds 70 characters instead of 160. One emoji triples the cost of a message.'
                              : 'GSM-7 encoding, 160 characters per segment.'
                          }
                        >
                          <Badge size="xs" color={t.sms_segments > 1 ? 'yellow' : 'gray'}>
                            {t.sms_segments} seg
                          </Badge>
                        </Tooltip>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{t.category}</Text>
                  </Table.Td>
                  <Table.Td>
                    {t.dlt_status === 'not_required' ? (
                      <Text size="xs" c="dimmed">—</Text>
                    ) : (
                      <Tooltip
                        label={t.dlt_rejection_reason ?? 'DLT / WhatsApp approval state'}
                        disabled={!t.dlt_rejection_reason}
                      >
                        <Badge color={DLT_COLOR[t.dlt_status] ?? 'gray'}>{t.dlt_status}</Badge>
                      </Tooltip>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {fmtRelative(t.updated_at)} · v{t.version}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </DataState>
    </>
  );
}
